import { Patient } from '@/types/patient';
import { apiClient } from './client';
import type { PatientOverview } from '@/types/patient-overview';

interface PatientsPagination {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasMore: boolean;
}

interface PatientsPaginatedResponse {
    data: Patient[];
    pagination: PatientsPagination;
}

interface PatientsListParams {
    page?: number;
    pageSize?: number;
    q?: string;
    sort?: 'name' | 'recent';
}

export interface PatientData {
    full_name: string;
    date_of_birth: string;
    city?: string;
    gender: string;
    education_level?: string;
    referring_professional?: string;
    email?: string;
    address?: string;
    phone?: string;
    emergency_contact_name?: string;
    emergency_contact_phone?: string;
    clinical_history?: string;
}

export const patientsApi = {
    list: async (params: PatientsListParams = {}): Promise<PatientsPaginatedResponse> => {
        const { page = 1, pageSize = 10, q, sort } = params;

        const response = await apiClient.get<PatientsPaginatedResponse>('/patients/', {
            params: {
                page,
                pageSize,
                q: q || undefined,
                sort: sort || undefined,
            },
        });

        return response as unknown as PatientsPaginatedResponse;
    },

    listAll: async (params: Omit<PatientsListParams, 'page'> = {}): Promise<Patient[]> => {
        const pageSize = params.pageSize ?? 50;
        let page = 1;
        let hasMore = true;
        const patients: Patient[] = [];

        while (hasMore) {
            const response = await patientsApi.list({
                ...params,
                page,
                pageSize,
            });

            patients.push(...response.data);
            hasMore = response.pagination.hasMore;
            page += 1;
        }

        return patients;
    },

    list_available: async (): Promise<Patient[]> => {
        const response = await apiClient.get<Patient[]>('/patients/available');
        return response.data;
    },

    get: async (id: string): Promise<Patient> => {
        const response = await apiClient.get<Patient>(`/patients/${id}`);
        return response.data;
    },

    getOverview: async (id: string): Promise<PatientOverview> => {
        const response = await apiClient.get<{ data: PatientOverview }>(`/patients/${id}/overview`);
        return (response as unknown as { data: PatientOverview }).data;
    },

    create: async (data: PatientData): Promise<Patient> => {
        const response = await apiClient.post<Patient>('/patients/', data);
        return response.data;
    },

    update: async (id: string, data: Partial<PatientData>): Promise<Patient> => {
        const response = await apiClient.put<Patient>(`/patients/${id}`, data);
        return response.data;
    },

    regeneratePhoto: async (id: string): Promise<void> => {
        await apiClient.patch(`/patients/${id}/photo`);
    },

    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/patients/${id}`);
    },
};
